import { JOBS_PORTAL_PATH } from './paths';

/**
 * Jobs portal sidebar / mobile menu: flat items grouped by label.
 * Each item includes `roles`: which workspace roles may see it
 * ('guest' = not yet linked as company or employee).
 */
export const JOBS_PORTAL_MENU = [
    {
        label: 'Overview',
        items: [
            {
                id: 'join-overview',
                name: 'Join & overview',
                href: `${JOBS_PORTAL_PATH}?section=setup`,
                icon: 'fa-regular fa-compass',
                roles: ['guest'],
            },
            {
                id: 'dashboard',
                name: 'Dashboard',
                href: `${JOBS_PORTAL_PATH}?section=setup`,
                icon: 'fa-solid fa-gauge-high',
                roles: ['company', 'employee'],
            },
        ],
    },
    {
        label: 'Organization',
        items: [
            {
                id: 'employees',
                name: 'Employees',
                href: `${JOBS_PORTAL_PATH}?section=setup&panel=employee`,
                icon: 'fa-solid fa-users',
                roles: ['company'],
            },
        ],
    },
    {
        label: 'Tasks & work',
        items: [
            {
                id: 'create-task',
                name: 'Create task',
                href: `${JOBS_PORTAL_PATH}?section=create`,
                icon: 'fa-regular fa-square-plus',
                roles: ['company'],
            },
            {
                id: 'tasks-assigned',
                name: 'Assigned to you',
                employeeName: 'My tasks',
                href: `${JOBS_PORTAL_PATH}?section=workspace&panel=tasks&view=assigned`,
                icon: 'fa-regular fa-id-badge',
                roles: ['employee'],
            },
            {
                id: 'history',
                name: 'History',
                href: `${JOBS_PORTAL_PATH}?section=history`,
                icon: 'fa-solid fa-clock-rotate-left',
                roles: ['company', 'employee'],
            },
            {
                id: 'earnings',
                name: 'Earnings',
                companyName: 'Payouts',
                href: `${JOBS_PORTAL_PATH}?section=earnings`,
                icon: 'fa-solid fa-coins',
                roles: ['company', 'employee'],
            },
            {
                id: 'my-profile',
                name: 'My profile',
                companyName: 'Company profile',
                href: `${JOBS_PORTAL_PATH}?section=profile`,
                icon: 'fa-regular fa-circle-user',
                roles: ['company', 'employee'],
            },
            {
                id: 'tasks-company',
                name: 'Created tasks',
                href: `${JOBS_PORTAL_PATH}?section=workspace&panel=tasks&view=company`,
                icon: 'fa-regular fa-building',
                roles: ['company'],
            },
            {
                id: 'tasks-disputed',
                name: 'Disputed',
                href: `${JOBS_PORTAL_PATH}?section=workspace&panel=tasks&view=disputed`,
                icon: 'fa-solid fa-triangle-exclamation',
                roles: ['company', 'employee'],
            },
        ],
    },
    {
        label: 'Activity',
        items: [
            {
                id: 'activity-log',
                name: 'Activity log',
                href: `${JOBS_PORTAL_PATH}?section=workspace&panel=activity`,
                icon: 'fa-solid fa-timeline',
                roles: ['company', 'employee'],
            },
            {
                id: 'stats',
                name: 'Stats',
                href: `${JOBS_PORTAL_PATH}?section=stats`,
                icon: 'fa-solid fa-chart-column',
                roles: ['employee'],
            },
        ],
    },
];

/** Nav label (supports role-specific overrides on menu items). */
export function getJobsPortalMenuItemName(item, role) {
    if (role === 'employee' && item.employeeName) return item.employeeName;
    if (role === 'company' && item.companyName) return item.companyName;
    return item.name;
}

/** @param {'guest' | 'company' | 'employee'} role */
export function filterJobsPortalMenu(role) {
    return JOBS_PORTAL_MENU.map((section) => ({
        ...section,
        items: section.items.filter((item) =>
            (item.roles || []).includes(role),
        ),
    })).filter((section) => section.items.length > 0);
}

export function parseJobsPortalQuery(search) {
    const raw = String(search || '').replace(/^\?/, '');
    const q = new URLSearchParams(raw);
    return {
        section: q.get('section') || 'setup',
        panel: q.get('panel') || '',
        view: q.get('view') || '',
    };
}

/** Active state for jobs portal menu (pathname must be jobs portal). */
export function isJobsPortalMenuItemActive(pathname, search, itemId) {
    if (pathname !== JOBS_PORTAL_PATH) return false;
    const { section, panel, view } = parseJobsPortalQuery(search);

    switch (itemId) {
        case 'join-overview':
        case 'dashboard':
            return (
                section === 'setup' &&
                (!panel || panel === 'overview')
            );
        case 'employees':
            return (
                section === 'setup' &&
                (panel === 'employee' || panel === 'add-employee')
            );
        case 'create-task':
            return section === 'create';
        case 'history':
            return section === 'history';
        case 'earnings':
            return section === 'earnings';
        case 'my-profile':
            return section === 'profile';
        case 'tasks-company':
            return (
                section === 'workspace' &&
                panel === 'tasks' &&
                (view === 'company' || view === 'my' || !view)
            );
        case 'tasks-assigned':
            return section === 'workspace' && panel === 'tasks' && view === 'assigned';
        case 'tasks-disputed':
            return section === 'workspace' && panel === 'tasks' && view === 'disputed';
        case 'activity-log':
            return section === 'workspace' && panel === 'activity';
        case 'stats':
            return section === 'stats';
        default:
            return false;
    }
}
